import React from 'react';
import {FormattedMessage} from 'react-intl';
import useQuery from '../../../useQuery';
import EmptyCartByServiceRedirect from '../empty-cart-by-service-redirect';
import {ErrorBoundary} from 'react-error-boundary';
import ErrorFallback from '../../../components/ErrorFallback';
import Skeleton from '../../../components/Skeleton';
import {useLocation} from 'react-router-dom';
import searchFilter from '../../../searchFilter';
import Order from '../impulse/order';
import OrderItem, {OrderItemDynamicQuantity, OrderItemRequestData} from '../impulse/order-item';
import EffectRedirect from '../../../components/EffectRedirect';
import Coupon from '../impulse/coupon';
import AuthorizationError from '../../../components/AuthorizationError';
import StemBalance from '../stem-balance';
import SuspenseFallback from '../impulse/suspense-fallback';
import {ContentWrapperWithEmptyState} from '../impulse/content-wrapper';
import FooterAction from '../impulse/footer-action';
import ImpulseOrderWrapper from '../impulse/order-wrapper';
import useP2pError from '../impulse/use-p2p-error';
import useInvalidEndpoints from '../impulse/use-invalid-endpoints';
import useCreateImpulse from '../impulse/use-create-impulse';
import useCheckIfStem from '../impulse/use-check-if-stem';
import ImpulseMessage from '../impulse/message';
import useImpulseService from '../impulse/use-impulse-service';
import useCoupon from '../impulse/use-coupon';
import useLand from '../impulse/use-land';
import OrderDetailsForm from '../impulse/order-details-form';
import {useLazyLoadQuery} from 'react-relay/hooks';

const ImpulsePage = ({state, dispatch, subtitle, message, showOrderItemQuantity, dynamicEnpointQuantity, oplId}) => {
    const location = useLocation();
    const [newImpulseId, setNewImpulseId] = React.useState();
    const {
        isRefreshing,
        setIsRefreshing,
        requestPicks,
        currentLandPicks,
        serviceId,
        coupon,
        orderData,
        setLandMetas,
        landExists
    } = useLand({state});
    const {serviceById} = useImpulseService({serviceId});
    const {setCode} = useCoupon({state, setLandMetas});
    const {p2pError} = useP2pError({serviceId});
    const {invalidEndpoints, addInvalidEndpoint, resetInvalidEndpoints} = useInvalidEndpoints({serviceId});
    const {isStemImpulse} = useCheckIfStem({serviceId, state});
    const {
        createImpulse,
        isMutationInFlight,
        grecaptcha,
        register,
        formState,
        setValue
    } = useCreateImpulse({dispatch, currentLandPicks, coupon, serviceId, setNewImpulseId, resetInvalidEndpoints, invalidEndpoints, oplId});

    if (newImpulseId) {
        return <EffectRedirect to={`../payment${searchFilter(location.search, {impulseId: newImpulseId})}`} replace/>;
    }

    return (
        <div>
            <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                <React.Suspense fallback={<SuspenseFallback/>}>
                    {isStemImpulse && <StemBalance/>}
                    <ContentWrapperWithEmptyState {...{isRefreshing, landExists}}>
                            <ErrorBoundary {...{FallbackComponent: AuthorizationError}}>
                                <ImpulseOrderWrapper {...{subtitle}}>
                                    <Order {...{...orderData, coupon, isStemImpulse}}>
                                        {currentLandPicks.map((item, index) =>
                                            <OrderItemRequestData {...{key: index, pick: item, endpointId: item.endpointId, serviceById, quantityInCart: item.quantity, addInvalidEndpoint}}>
                                                {({endpointById}) => 
                                                    dynamicEnpointQuantity ?
                                                    <OrderItemDynamicQuantity {...{service: serviceById, ...endpointById, untie: item.untie, quantity: item.quantity, dispatch, stateKey: item.key}}/>
                                                    :
                                                    <OrderItem {...{service: serviceById, ...endpointById, untie: item.untie, quantity: endpointById.quantity < item.quantity ? endpointById.quantity : item.quantity, showOrderItemQuantity}}/>
                                                }
                                            </OrderItemRequestData>
                                        )}
                                        {invalidEndpoints.map((item, index) => 
                                            <OrderItem {...{key: index, service: serviceById, ...item.endpointData, untie: item.untie, quantity: item.quantity, maxQuantity: 0, showOrderItemQuantity, invalid: true}}/>
                                        )}
                                        <Coupon {...{coupon, setCode, isRefreshing, setIsRefreshing, picks: requestPicks}}/>
                                    </Order>
                                </ImpulseOrderWrapper>
                                <ImpulseMessage {...{message}}/>
                                <div className="padding-bottom-2rem mw768-padding-bottom-2dot5rem">
                                    <p className="text-2xl semibold color-gray-900 padding-top-2rem mw768-padding-top-2dot5rem">
                                        <FormattedMessage defaultMessage='Contact details'/>
                                    </p>
                                    <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                                        <OrderDetailsForm {...{serviceId, registerFormField: register, formState, setValue}}/>
                                    </ErrorBoundary>
                                </div>
                                <FooterAction {...{createImpulse, isLoading: grecaptcha || isMutationInFlight || isRefreshing, actionDisabled: p2pError}}/>
                            </ErrorBoundary>
                    </ContentWrapperWithEmptyState>
                </React.Suspense>
            </ErrorBoundary>
        </div>
    )
};

const OPLImpulsePage = () => {
    const {oplId} = useQuery();
    const {basketById} = useLazyLoadQuery(
        graphql`
            query impulseOPLCheckoutPagesBasketByIdQuery($id: String) {
                basketById(id: $id) {
                    id
                    notes
                    picks {
                        endpoint {
                            id
                        }
                        initialQuantity
                        quantity
                        service {
                            id
                        }
                    }
                }
            }
        `,
        {id: oplId}
    );
    const basketPick = basketById.picks[0];
    const state = [{endpointId: basketPick.endpoint.id, key: "d7a47d48-3ce9-4e23-b6c2-2074a99cd6f8", quantity: basketPick.quantity, serviceId: basketPick.service.id}];
    const redirectToService = React.useMemo(() => {
        return basketPick.initialQuantity > basketPick.quantity && !!oplId;
    }, [oplId, basketPick]);
    const dispatch = () => {};
    const subtitle = <FormattedMessage defaultMessage='To reserve your order for 15 min. please enter your contact details and click the "Proceed with ordering" button'/>;
    const message = <FormattedMessage defaultMessage='The order will be sent to the specified email'/>;
    const showOrderItemQuantity = true;
    if (redirectToService) {
        return (
            <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                <React.Suspense fallback={
                    <div className='checkout-content block width-100percent margin-bottom-2rem'>
                        <div className='checkout-view display-flex flex-direction-column checkout-view--container padding-top-2rem'>
                            <Skeleton/>
                        </div>
                    </div>
                }>
                    <EmptyCartByServiceRedirect {...{serviceId: basketPick.service.id}}/>
                </React.Suspense>
            </ErrorBoundary>
        )
    };

    return (
        <ImpulsePage {...{state, dispatch, subtitle, message, showOrderItemQuantity, oplId}}/>
    )
};

export default React.memo(OPLImpulsePage);
